/*
  Cardmaking Glossary (/pages/cardmaking-glossary)

  Progressive enhancement over the page-builder markup. With this file blocked
  the page is still a readable A-Z list with working letter anchors; with it the
  sidebar index becomes navigable, filterable and aware of where you are.

    1. Give every term heading a stable id and turn the sidebar's plain <li>
       text into links pointing at it.
    2. Label the gold shop pills from their destination.
    3. Filter the index as you type.
    4. Highlight the letter you're currently reading.
    5. Collapse the index behind a toggle on mobile, where the rail stacks above
       4,000px of list.

  Ids are assigned here rather than in the template because the entries live in
  124 separate blocks; hand-authoring an anchor block per term would double the
  template's size for something derivable from the heading text.
*/
(function () {
  'use strict';

  var rail = document.querySelector('.glossary-rail');
  var main = document.querySelector('.glossary-main');
  if (!rail || !main) return;

  var MOBILE = '(max-width: 767.49px)';

  function slug(text) {
    return (
      'term-' +
      text
        .toLowerCase()
        .replace(/[‘’“”]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    );
  }

  /* ------------------------------------------------ 1. anchor every entry -- */

  var entriesByKey = {};
  var entries = [];

  Array.prototype.forEach.call(main.querySelectorAll('.glossary-entry'), function (entry) {
    var heading = entry.querySelector('h3');
    if (!heading) return;
    var label = heading.textContent.trim();
    if (!label) return;

    var id = slug(label);
    // "Photopolymer Stamps" is listed twice in the source index; keep the first.
    if (document.getElementById(id)) return;
    heading.id = id;

    entries.push(entry);
    entriesByKey[label.toLowerCase()] = id;
  });

  /* ------------------------------------------- 2. label the action buttons -- */

  // [test, button text, spoken label]. First match wins; the last entry is the
  // catch-all, so a plain product or collection link says "Shop".
  var ACTIONS = [
    [/youtube\.com|youtu\.be/, 'Watch', 'Watch a video about '],
    [/facebook\.com|instagram\.com/, 'See more', 'See more about '],
    [/\/blogs\//, 'Read more', 'Read more about '],
    [/\/pages\//, 'Learn more', 'Learn more about '],
    [/./, 'Shop this', 'Shop '],
  ];

  // An entry holds at most one Custom HTML block, and that block is the action
  // button -- the flex-item that used to carry .glossary-entry__shop was one of
  // the 475 wrappers dropped to get the template under Shopify's 512 KB limit.
  var SHOP_LINK = '.glossary-entry .page-custom-html-block a';

  Array.prototype.forEach.call(main.querySelectorAll(SHOP_LINK), function (link) {
    var href = link.getAttribute('href') || '';
    var action = ACTIONS.filter(function (a) {
      return a[0].test(href);
    })[0];
    link.setAttribute('data-cta', action[1]);

    // The generated labels read "Shop Watch" / "Shop Photo". Rebuild them from
    // the term so a screen reader hears "Watch a video about Z-Fold Card".
    var entry = link.closest('.glossary-entry');
    var heading = entry && entry.querySelector('h3');
    if (heading) link.setAttribute('aria-label', action[2] + heading.textContent.trim());
  });

  /* ---------------------------------------------- 3. link the index terms -- */

  var index = rail.querySelector('.glossary-index');
  var indexItems = [];
  var linkedCount = 0;

  if (index) {
    Array.prototype.forEach.call(index.querySelectorAll('li'), function (li) {
      var label = li.textContent.trim();
      var id = entriesByKey[label.toLowerCase()];
      var node;

      if (id) {
        node = document.createElement('a');
        node.href = '#' + id;
        linkedCount++;
      } else {
        // No entry on the page for this term -- render it as text, not a link
        // that goes nowhere.
        node = document.createElement('span');
      }
      node.textContent = label;
      li.textContent = '';
      li.appendChild(node);
      indexItems.push({ li: li, text: label.toLowerCase() });
    });

    // Flash the destination so a jump into a wall of similar entries is legible.
    index.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#term-"]');
      if (!link) return;
      var entry = document.getElementById(link.hash.slice(1));
      entry = entry && entry.closest('.glossary-entry');
      if (!entry) return;
      Array.prototype.forEach.call(main.querySelectorAll('.glossary-entry--target'), function (el) {
        el.classList.remove('glossary-entry--target');
      });
      entry.classList.add('glossary-entry--target');
    });
  }

  /* --------------------------------------------------- 4. the filter field -- */

  var az = rail.querySelector('.glossary-az');

  if (index && indexItems.length) {
    var filter = document.createElement('div');
    filter.className = 'glossary-filter';
    filter.innerHTML =
      '<svg class="glossary-filter__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '<circle cx="11" cy="11" r="7"></circle><path d="M20 20l-3.5-3.5"></path></svg>' +
      '<label class="visually-hidden" for="glossary-filter-input">Filter glossary terms</label>' +
      '<input class="glossary-filter__input" id="glossary-filter-input" type="search" ' +
      'autocomplete="off" placeholder="Filter terms…">' +
      '<p class="glossary-filter__count" role="status" aria-live="polite"></p>';

    if (az && az.nextSibling) az.parentNode.insertBefore(filter, az.nextSibling);
    else rail.querySelector('.glossary-rail__inner').insertBefore(filter, index.parentNode);

    var input = filter.querySelector('.glossary-filter__input');
    var count = filter.querySelector('.glossary-filter__count');
    // Each letter's "A" label and its <ul> are separate blocks, so hiding a
    // letter means hiding the heading block that precedes the list's block.
    var groups = [];
    Array.prototype.forEach.call(index.children, function (child) {
      if (child.querySelector('ul')) groups.push({ list: child, head: child.previousElementSibling });
    });

    var timer;
    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var query = input.value.trim().toLowerCase();
        var shown = 0;

        indexItems.forEach(function (item) {
          var match = !query || item.text.indexOf(query) !== -1;
          item.li.hidden = !match;
          if (match) shown++;
        });

        groups.forEach(function (group) {
          var empty = !group.list.querySelector('li:not([hidden])');
          group.list.hidden = empty;
          if (group.head) group.head.hidden = empty;
        });

        count.textContent = query
          ? shown + (shown === 1 ? ' term' : ' terms') + ' match “' + input.value.trim() + '”'
          : '';
        index.scrollTop = 0;
      }, 120);
    });
  }

  /* ------------------------------------------------------- 5. the scrollspy -- */

  var chips = Array.prototype.slice.call(rail.querySelectorAll('.glossary-az__item a'));
  var sections = Array.prototype.slice.call(main.querySelectorAll('.glossary-letter__title'));

  if (chips.length && sections.length) {
    // Letter sections are far taller than the viewport, so an IntersectionObserver
    // would sit idle for most of the scroll. Read positions instead: the active
    // letter is whichever heading last passed under the header.
    var syncActiveLetter = function () {
      var current = null;
      sections.forEach(function (section) {
        if (section.getBoundingClientRect().top <= 200) current = section.id;
      });
      chips.forEach(function (chip) {
        if (chip.hash.slice(1) === current) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
      });
    };

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          syncActiveLetter();
        });
      },
      { passive: true }
    );
    syncActiveLetter();
  }

  /* ------------------------------------------------- 6. mobile index toggle -- */

  if (index) {
    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'glossary-index-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'glossary-index');
    toggle.innerHTML =
      '<span>Browse all ' + linkedCount + ' terms</span>' +
      '<svg class="glossary-index-toggle__chevron" viewBox="0 0 12 12" fill="none" ' +
      'stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M2 4l4 4 4-4"></path></svg>';

    index.id = 'glossary-index';
    index.parentNode.insertBefore(toggle, index);

    var mq = window.matchMedia(MOBILE);
    var applyMobileState = function () {
      if (mq.matches) index.hidden = toggle.getAttribute('aria-expanded') !== 'true';
      else index.hidden = false;
    };

    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      applyMobileState();
    });

    if (mq.addEventListener) mq.addEventListener('change', applyMobileState);
    else if (mq.addListener) mq.addListener(applyMobileState);
    applyMobileState();

    // Tapping a term on mobile should close the list and reveal the entry.
    index.addEventListener('click', function (event) {
      if (!mq.matches) return;
      if (!event.target.closest('a[href^="#term-"]')) return;
      toggle.setAttribute('aria-expanded', 'false');
      applyMobileState();
    });
  }

  /* --------------------------------- 7. honour a deep link to a term anchor -- */

  if (location.hash.indexOf('#term-') === 0) {
    var deep = document.getElementById(location.hash.slice(1));
    if (deep) deep.scrollIntoView();
  }
})();
